import React, { useState, useCallback, useEffect, useRef } from 'react';
import { getApiUrl } from '../config/api';
import { getAuthToken } from '../utils/authUtils';
import { useLanguage } from '../contexts/LanguageContext';
import { MMEC_BRAND_COLORS } from '../utils/mmecBrandColors';
import { useTradingCall } from '../contexts/TradingCallContext';
import { addGeneratorStrategiesAlert } from '../utils/alertRaise';
import type { ScannerData } from '../types/ScannerData';
import type { TradingCall } from '../types/TradingCall';
import { isDesignSystemOn } from '../theme/mmecDsPreview';
import './TradingCallGeneratorPage.ds.css';

type ContextBundle = Record<string, unknown>;

/** Rows visible in the main table, filtered to focus symbols when possible (capped for the model context). */
function buildScannerSnapshotForTcg(rows: ScannerData[] | undefined, symbolCsv: string, maxRows: number): Record<string, unknown>[] {
  if (!rows?.length) return [];
  const want = symbolCsv
    .split(',')
    .map((s) => s.trim().toUpperCase())
    .filter(Boolean);
  let pick = rows;
  if (want.length > 0) {
    const set = new Set(want);
    const filtered = rows.filter((r) => {
      const u = String(r.Underlying || (r as { underlying_symbol?: string }).underlying_symbol || '').trim().toUpperCase();
      return set.has(u);
    });
    if (filtered.length) pick = filtered;
  }
  return pick.slice(0, maxRows) as unknown as Record<string, unknown>[];
}

interface TradingCallGeneratorPageProps {
  scannerData?: ScannerData[];
  dataMode?: 'accumulated' | '5min';
}

type StrategyRow = {
  symbol: string;
  strategy_type: string;
  rationale?: string;
  risk_notes?: string;
  invalidation?: string;
  target?: string;
  stop?: string;
  legs?: unknown[];
  risk_metrics?: Record<string, unknown>;
};

type Rec = {
  id: string;
  ts?: string;
  symbol: string;
  strategy_type: string;
  source?: string;
  outcome?: string | null;
  pnl?: number | null;
  r_multiple?: number | null;
  target?: string;
  stop?: string;
};

const OUTCOMES = ['target_hit', 'stop_loss', 'expired', 'manual_close', 'cancelled'] as const;

const WIZARD_STEPS = 4;

function callStatusToRecOutcome(status: TradingCall['status']): string {
  switch (status) {
    case 'TARGET_HIT':
      return 'target_hit';
    case 'STOP_LOSS':
      return 'stop_loss';
    case 'EXPIRED':
      return 'expired';
    case 'MANUAL_CLOSE':
      return 'manual_close';
    case 'CANCELLED':
      return 'cancelled';
    default:
      return 'manual_close';
  }
}

function rMultipleFromPaperDebit(call: TradingCall, pnl: number): number | null {
  const risk = Math.abs(call.entryPrice * call.quantity);
  if (!(risk > 0) || !Number.isFinite(risk)) return null;
  const r = pnl / risk;
  return Number.isFinite(r) ? Math.round(r * 1000) / 1000 : null;
}

type SmartFlowSnippet = { rows?: Array<{ symbol?: string }> };

function extractTopSymbolsFromContext(bundle: ContextBundle | null, limit: number): string[] {
  if (!bundle) return [];
  const flowSnippet = bundle['smart_flow_sample'] as SmartFlowSnippet | undefined; // audit-no-fake-data: allow (API field name)
  const rows = flowSnippet?.rows;
  if (!Array.isArray(rows)) return [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const row of rows) {
    const sym = String(row?.symbol || '')
      .trim()
      .toUpperCase();
    if (sym && !seen.has(sym)) {
      seen.add(sym);
      out.push(sym);
      if (out.length >= limit) break;
    }
  }
  return out;
}

async function authFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const token = getAuthToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    ...(init.headers as Record<string, string>),
  };
  if (token) headers.Authorization = `Bearer ${token}`;
  return fetch(getApiUrl(path), { ...init, headers });
}

const TradingCallGeneratorPage: React.FC<TradingCallGeneratorPageProps> = ({
  scannerData = [],
  dataMode = 'accumulated',
}) => {
  const { language } = useLanguage();
  const { addOllamaStrategiesToPortfolio, closedCalls } = useTradingCall();
  /** Avoid duplicate POSTs when backfilling store from closed paper trades */
  const paperPnlBackfillRef = useRef<Set<string>>(new Set());
  const ar = language === 'ar';

  const [wizardStep, setWizardStep] = useState(1);
  const [symbols, setSymbols] = useState('SPY,QQQ');
  const [userPrompt, setUserPrompt] = useState(
    ar
      ? 'اقترح أفكار استراتيجيات خيارات متوازنة بناءً على السياق، مع تحديد المخاطر.'
      : 'Propose balanced options strategy ideas based on the context, with clear risk notes.',
  );
  const [riskAppetite, setRiskAppetite] = useState<'conservative' | 'balanced' | 'aggressive'>('balanced');
  const [strategyFamily, setStrategyFamily] = useState<'income' | 'directional' | 'volatility' | 'mixed'>('mixed');
  const [includeElite, setIncludeElite] = useState(true);
  const [includeSmartFlow, setIncludeSmartFlow] = useState(true);
  const [includeFlowStatus, setIncludeFlowStatus] = useState(true);
  const [includeSnapshots, setIncludeSnapshots] = useState(true);
  const [includeHistoricalBars, setIncludeHistoricalBars] = useState(true);
  const [includeIntradayBars, setIncludeIntradayBars] = useState(true);
  const [includeFlowBias, setIncludeFlowBias] = useState(true);
  const [includeMarketRegime, setIncludeMarketRegime] = useState(true);
  const [snapshotHistoryDays, setSnapshotHistoryDays] = useState(14);
  const [primaryGenre, setPrimaryGenre] = useState<'balanced' | 'naked_only' | 'spreads_theta'>('balanced');
  const [optCovered, setOptCovered] = useState(false);
  const [optCollar, setOptCollar] = useState(false);
  const [optIronCondor, setOptIronCondor] = useState(false);
  const [optStraddle, setOptStraddle] = useState(false);
  const [optStrangle, setOptStrangle] = useState(false);
  const [optCalendar, setOptCalendar] = useState(false);
  const [flowLimit, setFlowLimit] = useState(40);
  const [persist, setPersist] = useState(true);
  const [provider, setProvider] = useState<'ollama' | 'deepseek'>('ollama');
  const [modelOverride, setModelOverride] = useState('');

  const [contextBundle, setContextBundle] = useState<ContextBundle | null>(null);
  const [contextLoading, setContextLoading] = useState(false);
  const [contextError, setContextError] = useState<string | null>(null);

  const [generateLoading, setGenerateLoading] = useState(false);
  const [generateError, setGenerateError] = useState<string | null>(null);
  const [summary, setSummary] = useState('');
  const [strategies, setStrategies] = useState<StrategyRow[]>([]);
  /** Aligned with `strategies` by index — from last successful generate (for paper portfolio ↔ store). */
  const [lastGenRecommendationIds, setLastGenRecommendationIds] = useState<(string | undefined)[]>([]);
  const [rawPreview, setRawPreview] = useState('');

  const [recommendations, setRecommendations] = useState<Rec[]>([]);
  const [recLoading, setRecLoading] = useState(false);
  const [filterSource, setFilterSource] = useState('trading_call_generator');
  const [outcomeEdits, setOutcomeEdits] = useState<
    Record<string, { outcome: string; pnl: string; r_multiple: string; notes: string }>
  >({});

  const fillSymbolsFromFlow = useCallback(() => {
    const top = extractTopSymbolsFromContext(contextBundle, 12);
    if (top.length) setSymbols(top.join(','));
  }, [contextBundle]);

  const buildGeneratorPreferences = useCallback(() => {
    const optional_structures: string[] = [];
    if (optCovered) optional_structures.push('covered_call');
    if (optCollar) optional_structures.push('collar');
    if (optIronCondor) optional_structures.push('iron_condor');
    if (optStraddle) optional_structures.push('straddle');
    if (optStrangle) optional_structures.push('strangle');
    if (optCalendar) optional_structures.push('calendar_spread');
    return { primary_genre: primaryGenre, optional_structures, notes: '' };
  }, [primaryGenre, optCovered, optCollar, optIronCondor, optStraddle, optStrangle, optCalendar]);

  const loadContext = useCallback(async () => {
    setContextLoading(true);
    setContextError(null);
    try {
      const body = {
        symbols: symbols.split(',').map((s) => s.trim()).filter(Boolean),
        include_elite: includeElite,
        include_smart_flow: includeSmartFlow,
        include_flow_status: includeFlowStatus,
        include_chain: includeSnapshots,
        include_historical_bars: includeHistoricalBars,
        include_intraday_bars: includeIntradayBars,
        include_flow_bias: includeFlowBias,
        flow_limit: flowLimit,
        scanner_snapshot: buildScannerSnapshotForTcg(scannerData, symbols, 120),
        generator_preferences: buildGeneratorPreferences(),
        include_market_regime: includeMarketRegime,
        snapshot_history_days: snapshotHistoryDays,
      };
      const res = await authFetch('/api/trading-call-generator/context', {
        method: 'POST',
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!data.success) {
        setContextError(data.error || 'Request failed');
        setContextBundle(null);
        return;
      }
      setContextBundle(data.context as ContextBundle);
    } catch (e) {
      setContextError(e instanceof Error ? e.message : 'Network error');
      setContextBundle(null);
    } finally {
      setContextLoading(false);
    }
  }, [
    symbols,
    includeElite,
    includeSmartFlow,
    includeFlowStatus,
    includeSnapshots,
    includeHistoricalBars,
    includeIntradayBars,
    includeFlowBias,
    flowLimit,
    scannerData,
    symbols,
    buildGeneratorPreferences,
    includeMarketRegime,
    snapshotHistoryDays,
  ]);

  const loadRecommendations = useCallback(async () => {
    setRecLoading(true);
    try {
      const q = filterSource.trim()
        ? `?limit=100&source=${encodeURIComponent(filterSource)}`
        : '?limit=100';
      const res = await authFetch(`/api/ai/strategy-recommendations${q}`);
      const data = await res.json();
      const recs = (data.recommendations || []) as Rec[];
      const gen = recs.filter((r) => (r.source || '') === 'trading_call_generator');
      setRecommendations(gen.length ? gen : recs);
    } catch {
      setRecommendations([]);
    } finally {
      setRecLoading(false);
    }
  }, [filterSource]);

  const generate = useCallback(async () => {
    setGenerateLoading(true);
    setGenerateError(null);
    setSummary('');
    setStrategies([]);
    setLastGenRecommendationIds([]);
    setRawPreview('');
    try {
      const body: Record<string, unknown> = {
        user_prompt: userPrompt,
        symbols: symbols.split(',').map((s) => s.trim()).filter(Boolean),
        include_elite: includeElite,
        include_smart_flow: includeSmartFlow,
        include_flow_status: includeFlowStatus,
        include_chain: includeSnapshots,
        include_historical_bars: includeHistoricalBars,
        include_intraday_bars: includeIntradayBars,
        include_flow_bias: includeFlowBias,
        flow_limit: flowLimit,
        persist,
        provider,
        risk_appetite: riskAppetite,
        strategy_family: strategyFamily,
        scanner_snapshot: buildScannerSnapshotForTcg(scannerData, symbols, 120),
        generator_preferences: buildGeneratorPreferences(),
        include_market_regime: includeMarketRegime,
        snapshot_history_days: snapshotHistoryDays,
      };
      if (modelOverride.trim()) body.model = modelOverride.trim();
      const res = await authFetch('/api/trading-call-generator/generate', {
        method: 'POST',
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!data.success) {
        setGenerateError(data.error || 'Generation failed');
        setRawPreview(typeof data.raw_model_text === 'string' ? data.raw_model_text : '');
        return;
      }
      setSummary(data.summary || '');
      const nextStrategies = Array.isArray(data.strategies) ? (data.strategies as StrategyRow[]) : [];
      setStrategies(nextStrategies);
      const recs = Array.isArray(data.recommendations) ? data.recommendations : [];
      setLastGenRecommendationIds(recs.map((r: { id?: string }) => r?.id));
      setRawPreview(typeof data.raw_model_text === 'string' ? data.raw_model_text : '');
      if (provider === 'ollama' && nextStrategies.length > 0) {
        addGeneratorStrategiesAlert({
          count: nextStrategies.length,
          symbols: nextStrategies.map((s) => s.symbol),
          ar,
        });
      }
      await loadRecommendations();
    } catch (e) {
      setGenerateError(e instanceof Error ? e.message : 'Network error');
    } finally {
      setGenerateLoading(false);
    }
  }, [
    userPrompt,
    symbols,
    includeElite,
    includeSmartFlow,
    includeFlowStatus,
    includeSnapshots,
    includeHistoricalBars,
    includeIntradayBars,
    includeFlowBias,
    flowLimit,
    persist,
    provider,
    riskAppetite,
    strategyFamily,
    modelOverride,
    loadRecommendations,
    addGeneratorStrategiesAlert,
    ar,
    scannerData,
    symbols,
    buildGeneratorPreferences,
    includeMarketRegime,
    snapshotHistoryDays,
  ]);

  useEffect(() => {
    loadRecommendations();
  }, [loadRecommendations]);

  /** If the server row has no PnL but a closed paper trade exists with the same recommendation id, persist realized PnL/R once. */
  useEffect(() => {
    if (!recommendations.length || !closedCalls.length) return;
    let cancelled = false;
    const run = async () => {
      const toSync: { id: string; body: Record<string, unknown> }[] = [];
      for (const r of recommendations) {
        if (r.pnl != null && Number.isFinite(Number(r.pnl))) continue;
        if (paperPnlBackfillRef.current.has(r.id)) continue;
        const match = closedCalls.find(
          (c) =>
            c.strategyRecommendationId === r.id &&
            c.performance?.pnl != null &&
            Number.isFinite(c.performance.pnl),
        );
        if (!match?.performance) continue;
        paperPnlBackfillRef.current.add(r.id);
        const pnl = match.performance.pnl as number;
        const rMult = rMultipleFromPaperDebit(match, pnl);
        toSync.push({
          id: r.id,
          body: {
            outcome: callStatusToRecOutcome(match.status),
            pnl,
            ...(rMult != null ? { r_multiple: rMult } : {}),
          },
        });
      }
      for (const item of toSync) {
        if (cancelled) return;
        try {
          const res = await authFetch(`/api/ai/strategy-recommendations/${encodeURIComponent(item.id)}/outcome`, {
            method: 'POST',
            body: JSON.stringify(item.body),
          });
          const data = await res.json();
          if (!data.success) paperPnlBackfillRef.current.delete(item.id);
        } catch {
          paperPnlBackfillRef.current.delete(item.id);
        }
      }
      if (!cancelled && toSync.length) {
        await loadRecommendations();
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [recommendations, closedCalls, loadRecommendations]);

  const exportCsv = async () => {
    try {
      const res = await authFetch(
        `/api/trading-call-generator/export?format=csv&limit=500&source=${encodeURIComponent(filterSource || 'trading_call_generator')}`,
      );
      if (!res.ok) return;
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'recommendations_export.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      /* ignore */
    }
  };

  const submitOutcome = async (id: string) => {
    const ed = outcomeEdits[id] || { outcome: 'manual_close', pnl: '', r_multiple: '', notes: '' };
    let pnlNum: number | null = null;
    if (ed.pnl.trim() !== '') {
      const p = parseFloat(ed.pnl);
      pnlNum = Number.isNaN(p) ? null : p;
    }
    let rNum: number | null = null;
    if (ed.r_multiple.trim() !== '') {
      const x = parseFloat(ed.r_multiple);
      rNum = Number.isNaN(x) ? null : x;
    }
    try {
      const res = await authFetch(`/api/ai/strategy-recommendations/${encodeURIComponent(id)}/outcome`, {
        method: 'POST',
        body: JSON.stringify({
          outcome: ed.outcome || 'manual_close',
          pnl: pnlNum,
          r_multiple: rNum,
          notes: ed.notes || '',
        }),
      });
      const data = await res.json();
      if (data.success) {
        await loadRecommendations();
      }
    } catch {
      /* ignore */
    }
  };

  // Design System preview: one gold primary (Generate); every other button is a surface-2 secondary.
  const ds = isDesignSystemOn();
  const cardClass = 'flex flex-col';
  const cardStyle: React.CSSProperties = { background: 'var(--elev)', border: '1px solid var(--hair)', borderRadius: 'var(--radius-md, 10px)', padding: 16 };
  const fieldClass = 'mt-1 w-full text-sm px-2 py-1 rounded border';
  const fieldStyle: React.CSSProperties = { background: 'var(--surface-2, var(--base))', borderColor: 'var(--line-strong, var(--hair))', color: 'var(--txt)' };
  const sel = fieldClass;
  const mutedStyle: React.CSSProperties = { color: 'var(--muted)' };
  const primaryBtnStyle: React.CSSProperties = ds
    ? { backgroundColor: 'var(--surface-2)', color: 'var(--text)', border: '1px solid var(--line)', fontWeight: 600 }
    : { backgroundColor: 'var(--acc)', color: 'var(--void)' };
  const preStyle: React.CSSProperties = { background: 'var(--void)', color: 'var(--txt-2)', fontFamily: 'var(--font-mono)' };
  const errorStyle: React.CSSProperties = {
    background: 'var(--down-dim)', color: 'var(--down)', border: '1px solid color-mix(in srgb, var(--down) 40%, transparent)',
    borderRadius: 8, padding: '8px 12px', fontSize: 12.5,
  };

  return (
    <div className={`min-h-screen p-4${ds ? ' tcg-root' : ''}`} dir={ds ? (ar ? 'rtl' : 'ltr') : undefined} style={{ background: 'var(--base)', color: 'var(--txt)', fontFamily: 'var(--font-ui)' }}>
      <div className="max-w-6xl mx-auto space-y-6">
        <header>
          <h1 style={{ fontSize: 18, fontWeight: 600, color: 'var(--txt)', margin: 0 }}>
            {ar ? 'مولد إشارات التداول' : 'Trading Call Generator'}
          </h1>
          <p className="mt-1" style={{ fontSize: 12.5, color: 'var(--muted)' }}>
            {ar
              ? 'سياق من التقرير والتدفق وسلسلة مختصرة اختيارية؛ النموذج يقترح أفكاراً فقط. ليس نصيحة استثمارية.'
              : 'Context from report, flow, and optional compact chain rows; the model proposes ideas only. Not investment advice.'}
          </p>
        </header>

        <div className="flex flex-wrap items-center gap-2 text-xs" style={mutedStyle}>
          <span>{ar ? 'الخطوة' : 'Step'}:</span>
          {Array.from({ length: WIZARD_STEPS }, (_, i) => i + 1).map((s) => (
            <button
              key={s}
              type="button"
              className={`mmec-btn${wizardStep === s ? ' is-active' : ''}`}
              onClick={() => setWizardStep(s)}
            >
              {s}
            </button>
          ))}
        </div>

        <section className={cardClass} style={cardStyle}>
          {wizardStep === 1 && (
            <div>
              <h2 className="text-sm font-semibold mb-3">
                {ar ? '1 — الكون والرموز' : '1 — Universe'}
              </h2>
              <label className="block text-xs">
                <span style={mutedStyle}>
                  {ar ? 'الرموز (مفصولة بفواصل)' : 'Symbols (comma-separated)'}
                </span>
                <input
                  className={fieldClass} style={fieldStyle}
                  value={symbols}
                  onChange={(e) => setSymbols(e.target.value)}
                />
              </label>
              <button
                type="button"
                className="mmec-btn mt-2"
                onClick={fillSymbolsFromFlow}
                disabled={!contextBundle}
              >
                {ar ? 'استخدم رموزاً من عينة التدفق' : 'Use symbols from flow sample'}
              </button>
            </div>
          )}

          {wizardStep === 2 && (
            <div>
              <h2 className="text-sm font-semibold mb-3">
                {ar ? '2 — المخاطر والعائد' : '2 — Risk & style'}
              </h2>
              <div className="grid gap-3 md:grid-cols-2">
                <label className="block text-xs">
                  <span style={mutedStyle}>
                    {ar ? 'الشهية للمخاطر' : 'Risk preference'}
                  </span>
                  <select
                    className={sel} style={fieldStyle}
                    value={riskAppetite}
                    onChange={(e) => setRiskAppetite(e.target.value as typeof riskAppetite)}
                  >
                    <option value="conservative">{ar ? 'محافظ' : 'Conservative'}</option>
                    <option value="balanced">{ar ? 'متوازن' : 'Balanced'}</option>
                    <option value="aggressive">{ar ? 'عدواني' : 'Aggressive'}</option>
                  </select>
                </label>
                <label className="block text-xs">
                  <span style={mutedStyle}>
                    {ar ? 'عائلة الاستراتيجية' : 'Strategy family'}
                  </span>
                  <select
                    className={sel} style={fieldStyle}
                    value={strategyFamily}
                    onChange={(e) => setStrategyFamily(e.target.value as typeof strategyFamily)}
                  >
                    <option value="income">{ar ? 'دخل' : 'Income'}</option>
                    <option value="directional">{ar ? 'اتجاهي' : 'Directional'}</option>
                    <option value="volatility">{ar ? 'تقلب' : 'Volatility'}</option>
                    <option value="mixed">{ar ? 'مختلط' : 'Mixed'}</option>
                  </select>
                </label>
              </div>
            </div>
          )}

          {wizardStep === 3 && (
            <div>
              <h2 className="text-sm font-semibold mb-3">
                {ar ? '3 — بيانات السياق' : '3 — Context data'}
              </h2>
              <div className="grid gap-3 md:grid-cols-2">
                <label className="block text-xs">
                  <span style={mutedStyle}>Flow limit</span>
                  <input
                    type="number"
                    min={10}
                    max={200}
                    className={fieldClass} style={fieldStyle}
                    value={flowLimit}
                    onChange={(e) => setFlowLimit(Number(e.target.value) || 40)}
                  />
                </label>
                <label className="block text-xs">
                  <span style={mutedStyle}>
                    {ar ? 'أيام لقطات مجمعة' : 'Snapshot history (days)'}
                  </span>
                  <input
                    type="number"
                    min={1}
                    max={30}
                    className={fieldClass} style={fieldStyle}
                    value={snapshotHistoryDays}
                    onChange={(e) => setSnapshotHistoryDays(Math.min(30, Math.max(1, Number(e.target.value) || 14)))}
                  />
                </label>
              </div>
              <p className="text-xs mt-2" style={mutedStyle}>
                {ar ? 'الجدول الحالي:' : 'Current table:'}{' '}
                {dataMode === '5min' ? (ar ? '٥ د' : '5m') : ar ? 'متراكم' : 'Accum.'} · {scannerData.length}{' '}
                {ar ? 'صفًا يُرسل مع الطلب.' : 'rows sent with each request (when available).'}
              </p>
              <div className="flex flex-wrap gap-4 mt-3 text-xs">
                <label className="inline-flex items-center gap-1 cursor-pointer">
                  <input type="checkbox" checked={includeElite} onChange={(e) => setIncludeElite(e.target.checked)} />
                  {ar ? 'تقرير النبضة' : 'Pulse report'}
                </label>
                <label className="inline-flex items-center gap-1 cursor-pointer">
                  <input type="checkbox" checked={includeSmartFlow} onChange={(e) => setIncludeSmartFlow(e.target.checked)} />
                  Top-premium tape sample
                </label>
                <label className="inline-flex items-center gap-1 cursor-pointer">
                  <input type="checkbox" checked={includeFlowStatus} onChange={(e) => setIncludeFlowStatus(e.target.checked)} />
                  {ar ? 'حالة المنصة' : 'Flow platform status'}
                </label>
                <label className="inline-flex items-center gap-1 cursor-pointer">
                  <input type="checkbox" checked={includeSnapshots} onChange={(e) => setIncludeSnapshots(e.target.checked)} />
                  {ar ? 'ملخص السلسلة/اليونان' : 'Chain / greeks summary'}
                </label>
                <label className="inline-flex items-center gap-1 cursor-pointer">
                  <input type="checkbox" checked={includeHistoricalBars} onChange={(e) => setIncludeHistoricalBars(e.target.checked)} />
                  {ar ? 'أداء السهم اليومي' : 'Underlying daily history'}
                </label>
                <label className="inline-flex items-center gap-1 cursor-pointer">
                  <input type="checkbox" checked={includeIntradayBars} onChange={(e) => setIncludeIntradayBars(e.target.checked)} />
                  {ar ? 'شموع داخل اليوم' : 'Intraday bars'}
                </label>
                <label className="inline-flex items-center gap-1 cursor-pointer">
                  <input type="checkbox" checked={includeFlowBias} onChange={(e) => setIncludeFlowBias(e.target.checked)} />
                  {ar ? 'انحياز صفوف التدفق' : 'Flow row bias'}
                </label>
                <label className="inline-flex items-center gap-1 cursor-pointer">
                  <input type="checkbox" checked={includeMarketRegime} onChange={(e) => setIncludeMarketRegime(e.target.checked)} />
                  {ar ? 'نظام السوق (تقلب + لقطات)' : 'Market regime (vol + snapshot history)'}
                </label>
                <label className="inline-flex items-center gap-1 cursor-pointer">
                  <input type="checkbox" checked={persist} onChange={(e) => setPersist(e.target.checked)} />
                  {ar ? 'حفظ الاقتراحات' : 'Save recommendations'}
                </label>
              </div>
            </div>
          )}

          {wizardStep === 4 && (
            <div>
              <h2 className="text-sm font-semibold mb-3">
                {ar ? '4 — النموذج والتعليمات' : '4 — Model & instructions'}
              </h2>
              <div className="mb-4 p-3 rounded-lg border border-dashed space-y-2" style={{ borderColor: 'var(--border)' }}>
                <p className="text-xs font-medium" style={{ color: 'var(--txt-2)' }}>
                  {ar ? 'تركيز الاستراتيجية' : 'Strategy focus'}
                </p>
                <label className="block text-xs">
                  <span style={mutedStyle}>
                    {ar ? 'النوع الأساسي' : 'Primary genre'}
                  </span>
                  <select
                    className={fieldClass} style={fieldStyle}
                    value={primaryGenre}
                    onChange={(e) => setPrimaryGenre(e.target.value as 'balanced' | 'naked_only' | 'spreads_theta')}
                  >
                    <option value="balanced">{ar ? 'متوازن (عاري + سبريد/ثيتا)' : 'Balanced (naked + spread/theta)'}</option>
                    <option value="naked_only">{ar ? 'عاري فقط' : 'Naked calls/puts only'}</option>
                    <option value="spreads_theta">{ar ? 'سبريد وثيتا (ائتمان أولاً)' : 'Spreads & theta (credit first)'}</option>
                  </select>
                </label>
                <p className="text-xs" style={mutedStyle}>
                  {ar ? 'هياكل إضافية (اختياري):' : 'Optional structures:'}
                </p>
                <div className="flex flex-wrap gap-3 text-xs">
                  <label className="inline-flex items-center gap-1 cursor-pointer">
                    <input type="checkbox" checked={optCovered} onChange={(e) => setOptCovered(e.target.checked)} />
                    {ar ? 'فرصة مغطاة' : 'Covered call'}
                  </label>
                  <label className="inline-flex items-center gap-1 cursor-pointer">
                    <input type="checkbox" checked={optCollar} onChange={(e) => setOptCollar(e.target.checked)} />
                    Collar
                  </label>
                  <label className="inline-flex items-center gap-1 cursor-pointer">
                    <input type="checkbox" checked={optIronCondor} onChange={(e) => setOptIronCondor(e.target.checked)} />
                    Iron condor
                  </label>
                  <label className="inline-flex items-center gap-1 cursor-pointer">
                    <input type="checkbox" checked={optStraddle} onChange={(e) => setOptStraddle(e.target.checked)} />
                    Straddle
                  </label>
                  <label className="inline-flex items-center gap-1 cursor-pointer">
                    <input type="checkbox" checked={optStrangle} onChange={(e) => setOptStrangle(e.target.checked)} />
                    Strangle
                  </label>
                  <label className="inline-flex items-center gap-1 cursor-pointer">
                    <input type="checkbox" checked={optCalendar} onChange={(e) => setOptCalendar(e.target.checked)} />
                    {ar ? 'تقويم' : 'Calendar'}
                  </label>
                </div>
              </div>
              <div className="grid gap-3 md:grid-cols-2 mb-3">
                <label className="block text-xs">
                  <span style={mutedStyle}>Provider</span>
                  <select className={sel} style={fieldStyle} value={provider} onChange={(e) => setProvider(e.target.value as 'ollama' | 'deepseek')}>
                    <option value="ollama">Ollama (local)</option>
                    <option value="deepseek">Cloud (structured JSON)</option>
                  </select>
                </label>
                <label className="block text-xs">
                  <span style={mutedStyle}>
                    {ar ? 'اسم النموذج (اختياري)' : 'Model override (optional)'}
                  </span>
                  <input
                    className={fieldClass} style={fieldStyle}
                    value={modelOverride}
                    onChange={(e) => setModelOverride(e.target.value)}
                    placeholder=""
                  />
                </label>
              </div>
              <label className="block text-xs">
                <span style={mutedStyle}>{ar ? 'التعليمات' : 'Instructions'}</span>
                <textarea
                  className="mt-1 w-full text-sm px-2 py-2 rounded border min-h-[100px]" style={fieldStyle}
                  value={userPrompt}
                  onChange={(e) => setUserPrompt(e.target.value)}
                />
              </label>
            </div>
          )}

          <div className="flex flex-wrap gap-2 mt-4 items-center">
            <button
              type="button"
              className="px-3 py-2 rounded text-sm font-medium disabled:opacity-50"
              style={primaryBtnStyle}
              onClick={() => setWizardStep((s) => Math.max(1, s - 1))}
              disabled={wizardStep <= 1}
            >
              {ar ? 'السابق' : 'Back'}
            </button>
            <button
              type="button"
              className="px-3 py-2 rounded text-sm font-medium disabled:opacity-50"
              style={primaryBtnStyle}
              onClick={() => setWizardStep((s) => Math.min(WIZARD_STEPS, s + 1))}
              disabled={wizardStep >= WIZARD_STEPS}
            >
              {ar ? 'التالي' : 'Next'}
            </button>
            <button
              type="button"
              className="px-4 py-2 rounded text-sm font-medium disabled:opacity-50"
              style={primaryBtnStyle}
              onClick={loadContext}
              disabled={contextLoading}
            >
              {contextLoading ? '…' : ar ? 'تحميل السياق' : 'Load context'}
            </button>
            <button
              type="button"
              className="px-4 py-2 rounded text-sm font-medium text-white"
              style={ds ? { backgroundColor: 'var(--gold)', color: 'var(--text-on-accent)', fontWeight: 600 } : { backgroundColor: MMEC_BRAND_COLORS.orange }}
              onClick={generate}
              disabled={generateLoading}
            >
              {generateLoading ? '…' : ar ? 'توليد' : 'Generate'}
            </button>
          </div>
          {contextError && <p className="text-xs mt-2" style={{ color: 'var(--warn)' }}>{contextError}</p>}
          {generateError && <p className="mt-2" style={errorStyle}>{generateError}</p>}
        </section>

        {contextBundle && (
          <section className={cardClass} style={cardStyle}>
            <h2 className="text-sm font-semibold mb-2">
              {ar ? 'معاينة السياق (مختصر)' : 'Context preview (truncated)'}
            </h2>
            <pre className="text-[11px] overflow-auto max-h-48 p-2 rounded" style={preStyle}>
              {JSON.stringify(contextBundle, null, 0).slice(0, 12000)}
            </pre>
          </section>
        )}

        {summary && (
          <section className={cardClass} style={cardStyle}>
            <h2 className="text-sm font-semibold mb-2">{ar ? 'الملخص' : 'Summary'}</h2>
            <p className="text-sm whitespace-pre-wrap">{summary}</p>
          </section>
        )}

        {strategies.length > 0 && (
          <section className={cardClass} style={cardStyle}>
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
              <h2 className="text-sm font-semibold">{ar ? 'الاستراتيجيات' : 'Strategies'}</h2>
              <button
                type="button"
                className="text-xs px-3 py-1.5 rounded font-medium shrink-0"
                style={primaryBtnStyle}
                onClick={() => addOllamaStrategiesToPortfolio(strategies, lastGenRecommendationIds)}
              >
                {ar ? 'إضافة الكل إلى المحفظة الورقية' : 'Add all to paper portfolio'}
              </button>
            </div>
            <div className="space-y-3">
              {strategies.map((s, i) => (
                <div key={i} className="p-3 rounded border" style={{ borderColor: 'var(--hair)', background: ds ? 'var(--surface-2)' : undefined, borderRadius: ds ? 'var(--radius-sm)' : undefined }}>
                  <div className="font-medium text-sm">
                    {s.symbol} — {s.strategy_type}
                  </div>
                  {s.rationale && <p className="text-xs mt-1 opacity-90">{s.rationale}</p>}
                  {s.risk_notes && (
                    <p className="text-xs mt-1" style={{ color: 'var(--warn)' }}>
                      {ar ? 'مخاطر: ' : 'Risk: '}
                      {s.risk_notes}
                    </p>
                  )}
                  {s.invalidation && (
                    <p className="text-xs mt-1">
                      {ar ? 'إبطال: ' : 'Invalidation: '}
                      {s.invalidation}
                    </p>
                  )}
                  <p className="text-xs mt-1">
                    {ar ? 'هدف' : 'Target'}: {s.target || '—'} | {ar ? 'وقف' : 'Stop'}: {s.stop || '—'}
                  </p>
                  {s.risk_metrics && Object.keys(s.risk_metrics).length > 0 && (
                    <pre className="text-[10px] mt-2 overflow-auto opacity-90">
                      {JSON.stringify(s.risk_metrics, null, 2)}
                    </pre>
                  )}
                  {s.legs && s.legs.length > 0 && (
                    <pre className="text-[10px] mt-2 overflow-auto">{JSON.stringify(s.legs, null, 2)}</pre>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {rawPreview && !strategies.length && (
          <section className={cardClass} style={cardStyle}>
            <h2 className="text-sm font-semibold mb-2">{ar ? 'نص النموذج' : 'Raw model output'}</h2>
            <pre className="text-[11px] overflow-auto max-h-64 p-2 rounded" style={preStyle}>{rawPreview}</pre>
          </section>
        )}

        <section className={cardClass} style={cardStyle}>
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
            <h2 className="text-sm font-semibold">
              {ar ? 'الأداء والمتابعة' : 'Performance & tracking'}
            </h2>
            <div className="flex flex-wrap gap-2">
              <input
                type="text"
                placeholder="source filter"
                className="text-xs px-2 py-1 rounded border w-36" style={fieldStyle}
                value={filterSource}
                onChange={(e) => setFilterSource(e.target.value)}
              />
              <button
                type="button"
                className="mmec-btn"
                onClick={loadRecommendations}
                disabled={recLoading}
              >
                {ar ? 'تحديث' : 'Refresh'}
              </button>
              <button
                type="button"
                className="mmec-btn"
                onClick={exportCsv}
              >
                {ar ? 'تصدير CSV' : 'Export CSV'}
              </button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs" style={{ textAlign: 'start' }}>
              <thead>
                <tr style={{ color: 'var(--muted)', fontSize: 11, letterSpacing: '0.03em' }}>
                  <th className="px-2.5 py-1.5">Symbol</th>
                  <th className="px-2.5 py-1.5">Strategy</th>
                  <th className="px-2.5 py-1.5">Source</th>
                  <th className="px-2.5 py-1.5">Outcome</th>
                  <th className="px-2.5 py-1.5 text-right">PnL</th>
                  <th className="px-2.5 py-1.5 text-right">R</th>
                  <th className="px-2.5 py-1.5"> </th>
                </tr>
              </thead>
              <tbody>
                {recommendations.map((r) => (
                  <tr key={r.id} className="border-t" style={{ borderColor: 'var(--hair)' }}>
                    <td className="px-2.5 py-1.5 font-medium">{r.symbol}</td>
                    <td className="px-2.5 py-1.5 max-w-[220px] truncate">{r.strategy_type}</td>
                    <td className="px-2.5 py-1.5">{r.source || '—'}</td>
                    <td className="px-2.5 py-1.5">{r.outcome || '—'}</td>
                    <td className="px-2.5 py-1.5 text-right" style={{ fontFamily: 'var(--font-mono)', fontVariantNumeric: 'tabular-nums' }}>
                      {r.pnl != null && Number.isFinite(Number(r.pnl)) ? Number(r.pnl).toFixed(2) : '—'}
                    </td>
                    <td className="px-2.5 py-1.5 text-right" style={{ fontFamily: 'var(--font-mono)', fontVariantNumeric: 'tabular-nums' }}>
                      {r.r_multiple != null && Number.isFinite(Number(r.r_multiple))
                        ? Number(r.r_multiple).toFixed(3)
                        : '—'}
                    </td>
                    <td className="px-2.5 py-1.5">
                      <div className="flex flex-wrap gap-1 items-center">
                        <select
                          className="text-[11px] px-1 py-0.5 rounded border" style={fieldStyle}
                          value={(outcomeEdits[r.id]?.outcome ?? r.outcome ?? 'manual_close') as string}
                          onChange={(e) =>
                            setOutcomeEdits((prev) => ({
                              ...prev,
                              [r.id]: {
                                ...(prev[r.id] || { outcome: 'manual_close', pnl: '', r_multiple: '', notes: '' }),
                                outcome: e.target.value,
                              },
                            }))
                          }
                        >
                          {OUTCOMES.map((o) => (
                            <option key={o} value={o}>
                              {o}
                            </option>
                          ))}
                        </select>
                        <input
                          type="text"
                          placeholder="PnL"
                          className="w-14 text-[11px] px-1 py-0.5 rounded border" style={fieldStyle}
                          value={outcomeEdits[r.id]?.pnl ?? (r.pnl != null && Number.isFinite(Number(r.pnl)) ? String(r.pnl) : '')}
                          onChange={(e) =>
                            setOutcomeEdits((prev) => ({
                              ...prev,
                              [r.id]: {
                                ...(prev[r.id] || { outcome: 'manual_close', pnl: '', r_multiple: '', notes: '' }),
                                pnl: e.target.value,
                              },
                            }))
                          }
                        />
                        <input
                          type="text"
                          placeholder="R"
                          className="w-12 text-[11px] px-1 py-0.5 rounded border" style={fieldStyle}
                          value={
                            outcomeEdits[r.id]?.r_multiple ??
                            (r.r_multiple != null && Number.isFinite(Number(r.r_multiple))
                              ? String(r.r_multiple)
                              : '')
                          }
                          onChange={(e) =>
                            setOutcomeEdits((prev) => ({
                              ...prev,
                              [r.id]: {
                                ...(prev[r.id] || { outcome: 'manual_close', pnl: '', r_multiple: '', notes: '' }),
                                r_multiple: e.target.value,
                              },
                            }))
                          }
                        />
                        <button
                          type="button"
                          className="text-[11px] px-2 py-0.5 rounded"
                          style={primaryBtnStyle}
                          onClick={() => submitOutcome(r.id)}
                        >
                          Save
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {recommendations.length === 0 && !recLoading && (
              <p className="text-xs mt-2" style={mutedStyle}>{ar ? 'لا توجد سجلات بعد.' : 'No records yet.'}</p>
            )}
            {recommendations.length > 0 && (
              <p className="text-xs mt-2 max-w-3xl" style={mutedStyle}>
                {ar
                  ? 'يُملأ الربح/الخسارة والـ R عند الحفظ هنا، أو تلقائياً عند إغلاق صفقة ورقية مربوطة (بعد «إضافة الكل إلى المحفظة الورقية» من توليد سابق). اضغط تحديثاً بعد الإغلاق.'
                  : 'PnL and R appear when you type them and Save, or when a linked paper trade closes (use Add all to paper portfolio after a generate run so rows link). Press Refresh after closing positions.'}
              </p>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default TradingCallGeneratorPage;
